import React, {forwardRef, useImperativeHandle, useState} from "react";
import {Dialog, Button, Intent} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import styled from "@emotion/styled";

export interface TemporaryPasswordDialogRef {
  open: (password: string) => void;
  close: () => void;
}

const TemporaryPasswordDialog = forwardRef<TemporaryPasswordDialogRef, object>((props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  useImperativeHandle(ref, () => ({
    open: (pwd: string) => {
      setPassword(pwd);
      setCopied(false);
      setIsOpen(true);
    },
    close: () => {
      setIsOpen(false);
      setPassword("");
      setCopied(false);
    }
  }));

  return (
    <Dialog
      isOpen={isOpen}
      title="Temporary Password"
      icon={<FontAwesomeIcon style={{paddingRight: "10px"}} icon={solid("key")} />}
      onClose={() => setIsOpen(false)}
      canOutsideClickClose={false}
      css={{width: "fit-content", minWidth: "400px"}}
    >
      <DialogContent data-testid="temporary-password-dialog">
        <DescriptionText>The temporary password is:</DescriptionText>
        <PasswordText>{password}</PasswordText>
        <DescriptionText>
          When the user enters this password, they will prompted to set up a new password. Please ensure the user that
          you’re giving this temporary password to is in fact the actual user, and not a phishing attempt.
        </DescriptionText>
        <Button
          intent={copied ? Intent.SUCCESS : Intent.PRIMARY}
          icon={copied ? "tick" : "clipboard"}
          onClick={() => {
            navigator.clipboard.writeText(password);
            setCopied(true);
          }}
        >
          {copied ? "Copied!" : "Copy to Clipboard"}
        </Button>
      </DialogContent>
    </Dialog>
  );
});

TemporaryPasswordDialog.displayName = "TemporaryPasswordDialog";

export default TemporaryPasswordDialog;

const DialogContent = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DescriptionText = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
  max-width: 400px;
`;

const PasswordText = styled.div`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
`;
